import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, fmtDateTime } from "@/components/badges";
import { LifeBuoy, Loader2, Plus, Send } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Support() {
  const { user } = useAuth();
  const { t: tr } = useTranslation();
  const isAdmin = !!(user?.is_super_admin || user?.is_admin);
  const qc = useQueryClient();
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "" });
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState("");

  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ["tickets", isAdmin ? "all" : "my"],
    queryFn: async () => (await api.get(isAdmin ? "/admin/tickets" : "/tickets/my")).data,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["tickets"] });

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/tickets", form)).data,
    onSuccess: () => {
      toast.success(tr("support.toastCreated"));
      setNewOpen(false);
      setForm({ subject: "", message: "" });
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const replyMut = useMutation({
    mutationFn: async (id) => api.post(`/tickets/${id}/replies`, { text: reply }),
    onSuccess: () => {
      toast.success(isAdmin ? tr("support.toastReplyAdmin") : tr("support.toastReplyUser"));
      setReply("");
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const statusMut = useMutation({
    mutationFn: async ({ id, status }) => api.put(`/admin/tickets/${id}`, { status }),
    onSuccess: () => {
      toast.success(tr("support.toastStatusUpdated"));
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  return (
    <div className="space-y-6" data-testid="support-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">{tr("support.title")}</h1>
          <p className="text-sm text-slate-500">
            {isAdmin
              ? tr("support.subtitleAdmin")
              : tr("support.subtitleUser")}
          </p>
        </div>
        <Button onClick={() => setNewOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="new-ticket-button">
          <Plus className="mr-2 h-4 w-4" /> {tr("support.newTicket")}
        </Button>
      </div>

      {isLoading && <p className="text-sm text-slate-500" data-testid="tickets-loading">{tr("common.loading")}</p>}
      {!isLoading && tickets.length === 0 && (
        <Card className="border-dashed" data-testid="tickets-empty">
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <LifeBuoy className="h-8 w-8 text-slate-300" />
            <p className="text-sm font-medium text-slate-700">{tr("support.empty")}</p>
            <p className="text-xs text-slate-500">{tr("support.emptyBody")}</p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {tickets.map((t) => (
          <Card key={t.id} data-testid={`ticket-row-${t.id}`}>
            <CardContent className="p-4">
              <div
                role="button"
                tabIndex={0}
                className="flex w-full cursor-pointer flex-wrap items-center gap-3 text-left"
                onClick={() => setOpenId(openId === t.id ? null : t.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setOpenId(openId === t.id ? null : t.id);
                }}
                data-testid={`ticket-toggle-${t.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-900">{t.subject}</div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {isAdmin && <span className="font-medium text-slate-600">{t.user_name} · {t.user_email} · </span>}
                    {fmtDateTime(t.created_at)} · {tr("support.messagesCount", { count: (t.messages || []).length })}
                  </div>
                </div>
                {isAdmin ? (
                  <Select value={t.status} onValueChange={(v) => statusMut.mutate({ id: t.id, status: v })}>
                    <SelectTrigger className="h-8 w-36 text-xs" onClick={(e) => e.stopPropagation()} data-testid={`ticket-status-${t.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open" data-testid={`ticket-status-open-${t.id}`}>{tr("support.statusOpen")}</SelectItem>
                      <SelectItem value="in_review" data-testid={`ticket-status-in-review-${t.id}`}>{tr("support.statusInReview")}</SelectItem>
                      <SelectItem value="closed" data-testid={`ticket-status-closed-${t.id}`}>{tr("support.statusClosed")}</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <StatusBadge status={t.status} data-testid={`ticket-status-badge-${t.id}`} />
                )}
              </div>

              {openId === t.id && (
                <div className="mt-4 space-y-2 border-t pt-4" data-testid={`ticket-thread-${t.id}`}>
                  {(t.messages || []).map((m) => (
                    <div key={`${m.created_at}-${m.from}`} className={`max-w-2xl rounded-lg border px-3 py-2 text-sm ${m.from === "pruva" ? "ml-auto border-sky-200 bg-sky-50" : "bg-white"}`}>
                      <div className="mb-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                        {m.author} · {fmtDateTime(m.created_at)}
                      </div>
                      <div className="whitespace-pre-wrap text-slate-700">{m.text}</div>
                    </div>
                  ))}
                  {t.status !== "closed" && (
                    <div className="flex items-start gap-2 pt-2">
                      <Textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder={isAdmin ? tr("support.replyPhAdmin") : tr("support.replyPhUser")}
                        className="min-h-[70px]"
                        data-testid={`ticket-reply-input-${t.id}`}
                      />
                      <Button onClick={() => replyMut.mutate(t.id)} disabled={!reply.trim() || replyMut.isPending} data-testid={`ticket-reply-submit-${t.id}`}>
                        {replyMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                  )}
                  {t.status === "closed" && <p className="text-xs italic text-slate-400">{tr("support.ticketClosed")}</p>}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="new-ticket-dialog">
          <DialogHeader><DialogTitle>{tr("support.newTicketTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{tr("common.subject")}</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="ticket-subject-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{tr("support.messageL")}</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="min-h-[120px]" data-testid="ticket-message-input" />
              <p className="text-xs text-slate-500">{tr("support.ticketNote")}</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => createMut.mutate()} disabled={!form.subject.trim() || !form.message.trim() || createMut.isPending} data-testid="ticket-submit-button">
              {createMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {tr("support.submitTicket")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
